import React, { useEffect, useState } from 'react';
import { useQuestionToggleAtom } from '../../atoms/chat/useQuestionToggleAtom';
import { useSelectedTabAtom } from '../../atoms/chat/useSelectedTabAtom';
import { useAtom } from 'jotai';
import { ALL_FOLDERS, useChatEnvironmentAtom } from '../../atoms/chat/useChatEnvironmentAtom';
import { useActiveSessionAtom } from '../../atoms/chat/useActiveSessionAtom';
import { useMessageHistoryAtom } from '../../atoms/chat/useMessageHistoryAtom';
import { useFormButtonEnabledAtom } from '../../atoms/chat/useFormButtonEnabledAtom';
import { useFormInputEnabledAtom } from '../../atoms/chat/useFormInputEnabledAtom';
import { getChatSessions, getChatMessages, deleteChatSession, pinChatSession, renameChatSession } from '../../api/chatHistoryAPIs';
import { getFavouriteQuestions, deleteFavouriteQuestion, publishFavouriteQuestion, saveFavouriteQuestion } from '../../api/questionAPIs';
import { useFavouriteQuestionsAtom } from '../../atoms/chat/useFavouriteQuestionsAtom';
import useAuth from '../../hooks/useAuth';
import { selectedQuestionAtom } from '../../atoms/chat/useSelectedQuestionAtom';
import { useTranslation } from 'react-i18next';
import useCookie from '../../hooks/useCookie';
import { newUuid } from '../../utils/uuid';
import { useTabItemIdAtom } from '../../atoms/chat/useTabItemIdAtom';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import TruncatedText from '../shared/TruncatedText';

// Tenká vektorová pin ikona (štýl ChatGPT) – statická, bez prepínania pri hoveri.
// Rotácia 45° je zapečená v SVG, strike čiara sa zobrazí cez CSS pri hoveri (un-pin).
const PinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <g transform="rotate(45 12 12)">
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </g>
    <line className="pin-strike" x1="4.5" y1="4.5" x2="19.5" y2="19.5" strokeWidth="2" />
  </svg>
);

const ChatHistory = () => {
  const { auth } = useAuth();
  const [chatSessions, setChatSessions] = useState([]);
  const [t, i18n] = useTranslation('global');

  const [selectedTab] = useSelectedTabAtom();
  const [isQuestionsVisible, setIsQuestionsVisible] = useQuestionToggleAtom();
  const [, setFormButtonEnabled] = useFormButtonEnabledAtom();
  const [, setFormInputEnabled] = useFormInputEnabledAtom();
  const [chatEnvironmentId, setChatEnvironmentId] = useChatEnvironmentAtom();
  // obľúbená otázka pri „Všetky moje priečinky“ sa ukladá bez priečinka
  const questionEnvId = String(chatEnvironmentId) === ALL_FOLDERS ? null : chatEnvironmentId;
  const [tabItemId, ] = useTabItemIdAtom();

  const [favQuestions, setFavQuestions] = useFavouriteQuestionsAtom();
  const [, setSelectedQuestion] = useAtom(selectedQuestionAtom);
  const [questionToDelete, setQuestionToDelete] = useState(null); // { id, text }
  const [sharedOpen, setSharedOpen] = useState(true); // zdieľané sú defaultne zobrazené, dajú sa schovať
  const [panelTab, setPanelTab] = useState('history'); // 'history' | 'questions' — prepínač hore v paneli

  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [animatingTitle, setAnimatingTitle] = useState(null); // { sessionId, displayedText, fullText }
  const [menuState, setMenuState] = useState(null); // { id, top, left }
  const [renamingId, setRenamingId] = useState(null); // session sa práve premenováva
  const [renameValue, setRenameValue] = useState('');
  const [, setEnvCookie] = useCookie('ENV');

  // Zatvor kontextové menu pri kliknutí mimo alebo pri scrollovaní zoznamu
  useEffect(() => {
    if (!menuState) return;
    const close = () => setMenuState(null);
    document.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menuState]);

  const [activeSessionId, setActiveSessionId] = useActiveSessionAtom();
  const [, setMessageHistory] = useMessageHistoryAtom(i18n.language || 'en');


  const fetchChatSessions = () => {
    if (auth?.uid) {
      getChatSessions(auth.uid).then(sessions => {
         console.log('Loaded sessions from BE:', sessions);
         setChatSessions(sessions || []);
      }).catch(err => console.log('Error fetching sessions:', err));
    }
  };

  const normalizeTable = (table) => {
    if (!table) return null;
    const rawColumns = table.Columns || table.columns || [];
    const columns = rawColumns.map(col => ({
      Name: col.Name || col.name,
      DatabaseType: col.DatabaseType || col.databaseType,
      FieldType: col.FieldType || col.fieldType,
    }));
    const rows = table.Rows || table.rows || [];
    return { Columns: columns, Rows: rows };
  };

  const handleSessionClick = (sessionId) => {
    console.log('[ChatHistory] session clicked:', sessionId);
    getChatMessages(sessionId).then(messages => {
      console.log('[ChatHistory] messages loaded:', messages?.length);
      setActiveSessionId(sessionId);
      
      const mappedChat = messages.map(msg => {
        const meta = msg.metadata || msg.Metadata || {};
        const table = normalizeTable(meta.table || meta.Table);
        const msgType = msg.contentType !== undefined ? msg.contentType : msg.ContentType;
        const rawContent = msgType === 1 ? table : (msg.content || msg.Content || '');
        const translatedContent = msgType === 4 && typeof rawContent === 'string' ? t(rawContent) : rawContent;
        return {
          id: msg.id || msg.Id,
          Role: msg.chatRole || msg.ChatRole,
          Type: msgType,
          Rating: msg.rating ?? msg.Rating ?? null,
          Message: translatedContent,
          Table: table,
          SystemMessage: meta.systemMessage || meta.SystemMessage,
          GeneratedSQL: meta.generatedSQL || meta.GeneratedSQL,
          MessageID: msg.messageId || msg.MessageId,
          Metadata: meta.userDocsMessages || meta.UserDocsMessages,
          EnvironmentType: meta.docsEnvId || meta.DocsEnvId,
          sent: new Date(msg.createdAt || msg.CreatedAt || new Date()).toISOString()
        };
      });

      // Zapamätaj priečinok chatu: pri otvorení prepni docsEnv na ten, ktorému chat patrí,
      // aby ďalšie otázky išli proti správnemu priečinku (+ ENV cookie prežije reload).
      if (selectedTab === 'PromptEmbeddings') {
        const envIds = mappedChat.map(m => m.EnvironmentType).filter(v => v !== undefined && v !== null);
        const chatEnvId = envIds.length ? envIds[envIds.length - 1] : null;
        if (chatEnvId != null && String(chatEnvId) !== String(chatEnvironmentId)) {
          setChatEnvironmentId(String(chatEnvId));
          setEnvCookie('ENV', String(chatEnvId));
        }
      }

      setMessageHistory(prev => {
        return prev.map((item) => {
          if (item.intent === selectedTab) {
            return {
              ...item,
              chat: mappedChat,
            };
          }
          return item;
        });
      });
      togglePanel();
    });
  };

  // Add an event listener to refresh sessions if a chatSent event matches
  useEffect(() => {
    const handleChatSent = () => {
      fetchChatSessions();
    };
    window.addEventListener('chatSent', handleChatSent);
    return () => {
      window.removeEventListener('chatSent', handleChatSent);
    };
  }, [auth?.uid]);

  useEffect(() => {
    const handleTitleGenerated = (e) => {
      const { sessionId, title } = e.detail;
      setChatSessions(prev => prev.map(s =>
        (s.id || s.Id) === sessionId ? { ...s, title } : s
      ));
      setAnimatingTitle({ sessionId, displayedText: '', fullText: title });
    };
    window.addEventListener('sessionTitleGenerated', handleTitleGenerated);
    return () => window.removeEventListener('sessionTitleGenerated', handleTitleGenerated);
  }, []);

  useEffect(() => {
    if (!animatingTitle || animatingTitle.displayedText === animatingTitle.fullText) {
      if (animatingTitle) setAnimatingTitle(null);
      return;
    }
    const timer = setTimeout(() => {
      setAnimatingTitle(prev => ({
        ...prev,
        displayedText: prev.fullText.slice(0, prev.displayedText.length + 1),
      }));
    }, 40);
    return () => clearTimeout(timer);
  }, [animatingTitle]);

  useEffect(() => {
    fetchChatSessions();
  }, [selectedTab, chatEnvironmentId, tabItemId, auth?.uid]);

  // Načíta uložené otázky z BE (autoritatívny zdroj) — používa sa pri zmene tabu aj po zmene (publish/delete)
  const loadQuestions = () => {
    if (!auth?.uid) return;
    getFavouriteQuestions(selectedTab, i18n.language)
      .then(data => setFavQuestions(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => {
    loadQuestions();
  }, [selectedTab, chatEnvironmentId, auth?.uid]);

  const togglePanel = () => {
    if(window.screen.width <= 768){
      setIsQuestionsVisible((prevState) => !prevState);
    }
  }

  const handleQuestionClick = (q) => {
    handleNewChat();
    setTimeout(() => setSelectedQuestion(q.Question), 50);
  };

  // Zdieľanie = vytvorí VEREJNÚ KÓPIU otázky; pôvodná súkromná ostáva v „Moje otázky"
  const handleShareCopy = (e, q) => {
    e.stopPropagation();
    saveFavouriteQuestion(selectedTab, q.Question, i18n.language, questionEnvId)
      .then(res => {
        const newId = res && res.Data && res.Data.Id;
        return newId ? publishFavouriteQuestion(newId, true) : null;
      })
      .then(() => loadQuestions())
      .catch(err => console.error('Error sharing question:', err));
  };

  const handleDeleteQuestion = (e, q) => {
    e.stopPropagation();
    setQuestionToDelete({ id: q.Id, text: q.Question });
  };

  // Skopíruje cudziu zdieľanú otázku do mojich (súkromná kópia)
  const handleAddToMyList = (e, q) => {
    e.stopPropagation();
    saveFavouriteQuestion(selectedTab, q.Question, i18n.language, questionEnvId)
      .then(() => loadQuestions())
      .catch(err => console.error('Error adding to my list:', err));
  };

  const handleConfirmDeleteQuestion = () => {
    if (!questionToDelete) return;
    const id = questionToDelete.id;
    setFavQuestions(prev => (Array.isArray(prev) ? prev : []).filter(x => x.Id !== id));
    deleteFavouriteQuestion(id)
      .then(() => loadQuestions())
      .catch(err => { console.error('Error deleting question:', err); loadQuestions(); })
      .finally(() => setQuestionToDelete(null));
  };

  const getDefaultMessage = (intent, lang) => {
    const defaultLang = lang || 'en';
    if (defaultLang === 'sk') {
      if (intent === 'PromptData') return 'Ahoj, vitajte, ja som Millada! Opýtajte sa ma na niečo o vašich dátach. 😄';
      if (intent === 'PromptEmbeddings') return 'Ahoj, ako Ti môžem pomôcť s dokumentami?';
      return 'Ahoj, ako Ti môžem pomôcť?';
    } else if (defaultLang === 'cz') {
      if (intent === 'PromptData') return 'Ahoj, vítejte, já jsem Millada! Zeptejte se mě na cokoli ohledně vašich dat. 😄';
      if (intent === 'PromptEmbeddings') return 'Ahoj, jak vám mohu pomoci s dokumenty?';
      return 'Ahoj, jak Ti můžu pomoci?';
    } else {
      if (intent === 'PromptData') return 'Hi, welcome I am Millada! Go ahead and ask me something about your data. 😄';
      if (intent === 'PromptEmbeddings') return 'Here, I can help you with your documents.';
      return 'Hi, how can I help you?';
    }
  };

  const handleNewChat = () => {
    setActiveSessionId(0);
    setMessageHistory(prev => {
      return prev.map((item) => {
        if (item.intent === selectedTab) {
          const welcome = getDefaultMessage(selectedTab, i18n.language);
          // Uvítanie už na obrazovke je -> ponechaj jeho React kľúč (uiKey), inak by sa bublina
          // zahodila a znova "vyskočila" (napr. klik na uloženú otázku v novom chate).
          // id je nové, aby sa uvítanie v novej konverzácii uložilo.
          const first = item.chat && item.chat[0];
          const uiKey = first && first.Role === 'assistant' && first.Message === welcome
            ? (first.uiKey || first.id) : undefined;
          return {
            ...item,
            chat: [{
              id: Math.random().toString(36).substr(2, 9),
              uiKey,
              MessageID: newUuid(),
              Role: 'assistant',
              Type: 0,
              Message: welcome,
              sent: new Date().toISOString()
            }],
          };
        }
        return item;
      });
    });
    togglePanel();
  };

  const handlePinSession = (e, sessionId) => {
    e.stopPropagation();
    pinChatSession(sessionId)
      .then((updated) => {
        setChatSessions(prev =>
          prev.map(s => (s.id || s.Id) === sessionId ? { ...s, pinOrder: updated.pinOrder } : s)
            .sort((a, b) => {
              const aPin = a.pinOrder ?? null;
              const bPin = b.pinOrder ?? null;
              if (aPin !== null && bPin !== null) return aPin - bPin;
              if (aPin !== null) return -1;
              if (bPin !== null) return 1;
              return 0;
            })
        );
      })
      .catch(err => console.error('Error pinning session:', err));
  };

  const handleMenuToggle = (e, sessionId) => {
    e.stopPropagation();
    if (menuState && menuState.id === sessionId) {
      setMenuState(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_W = 170;
    const MENU_H = 52; // približná výška pre jednu položku
    // pravým okrajom zarovnáme pod tlačidlo; ak je málo miesta dole, otvor hore
    const openUp = rect.bottom + MENU_H > window.innerHeight;
    const left = Math.max(8, rect.right - MENU_W);
    const top = openUp ? rect.top - MENU_H - 4 : rect.bottom + 4;
    setMenuState({ id: sessionId, top, left });
  };

  const handleDeleteSession = (sessionId, sessionTitle) => {
    setSessionToDelete({ id: sessionId, title: sessionTitle });
  };

  const handleConfirmDelete = () => {
    if (sessionToDelete) {
      deleteChatSession(sessionToDelete.id).then(() => {
        setChatSessions(prev => prev.filter(s => (s.id || s.Id) !== sessionToDelete.id));
        // Reset if we deleted the active one
        setActiveSessionId(prevId => {
          if (prevId === sessionToDelete.id) {
            handleNewChat();
            return 0; // handleNewChat sets it to 0, just to be sure
          }
          return prevId;
        });
        setSessionToDelete(null);
      }).catch(err => {
        console.error("Error deleting session:", err);
        setSessionToDelete(null);
      });
    }
  };

  const handleCancelDelete = () => {
    setSessionToDelete(null);
  };

  const handleRenameSubmit = (sId) => {
    const val = (renameValue || '').trim();
    const cur = chatSessions.find(s => (s.id || s.Id) === sId);
    const curTitle = cur ? (cur.title || cur.Title) : '';
    setRenamingId(null);
    if (!val || val === curTitle) return; // prázdne/nezmenené -> zruš
    renameChatSession(sId, val)
      .then(() => setChatSessions(prev => prev.map(s =>
        (s.id || s.Id) === sId ? { ...s, title: val } : s)))
      .catch(err => console.error('Error renaming session:', err));
  };

  const getVisibleSessions = () => {
    const visible = chatSessions.filter(s => {
      // API may return intent or Intent, handle both safely
      const sIntent = s.intent || s.Intent || '';

      // Ak chceme striktne filtrovať podľa intentu, musíme ho mať vrátený z BE.
      // Ak intent máme, musí presne sedieť s vybranou záložkou.
      // Ak ho náhodou nemáme (string je prázdny), ponechávam fallback,
      // inak by sa nezobrazilo vôbec nič.
      if (sIntent && sIntent !== selectedTab) return false;

      // Ochrana proti `undefined` alebo `null` contextMetadata + podpora JSON/object formatu
      const rawMeta = s.contextMetadata !== undefined ? s.contextMetadata : s.ContextMetadata;
      let docsEnvFromMeta = null;
      let tabItemFromMeta = null;
      let cMeta = (rawMeta === null || rawMeta === undefined) ? '' : String(rawMeta);

      if (rawMeta && typeof rawMeta === 'object') {
        docsEnvFromMeta = rawMeta.docsEnvId ?? rawMeta.DocsEnvId ?? null;
        tabItemFromMeta = rawMeta.tabItemId ?? rawMeta.TabItemId ?? null;
      } else if (typeof rawMeta === 'string' && rawMeta !== '' && rawMeta !== 'null') {
        try {
          const parsedMeta = JSON.parse(rawMeta);
          docsEnvFromMeta = parsedMeta.docsEnvId ?? parsedMeta.DocsEnvId ?? null;
          tabItemFromMeta = parsedMeta.tabItemId ?? parsedMeta.TabItemId ?? null;
        } catch (_) {
          // Backward compatibility: raw string metadata may contain direct context id
          cMeta = rawMeta;
        }
      }

      if (selectedTab === 'PromptEmbeddings' && chatEnvironmentId) {
        if (docsEnvFromMeta !== null && docsEnvFromMeta !== undefined) {
          return String(docsEnvFromMeta) === String(chatEnvironmentId);
        }
        return cMeta === '' || cMeta === 'null' || cMeta === String(chatEnvironmentId);
      }
      if (selectedTab === 'PromptData' && tabItemId) {
        if (tabItemFromMeta !== null && tabItemFromMeta !== undefined) {
          return String(tabItemFromMeta) === String(tabItemId);
        }
        return cMeta === '' || cMeta === 'null' || cMeta === String(tabItemId);
      }
      // Chat tab (PromptGpt) atď
      return true;
    });

    return visible;
  };

  const questionList = Array.isArray(favQuestions) ? favQuestions : [];
  // Uložené = moje súkromné (nezverejnené); Zdieľané = všetky zverejnené (moje aj cudzie)
  const ownPrivate = questionList.filter(q => q.IsOwner && !q.IsPublic && q.Intent === selectedTab);
  const sharedAll = questionList.filter(q => q.IsPublic && q.Intent === selectedTab);

  return (
    <div id="questionPanel" className={`question-panel ${isQuestionsVisible ? 'open' : 'close'}`}>

      {/* ── Nový chat (zvýraznené tlačidlo) + prepínač: História | Otázky ─── */}
      <div className="panel-tabs-bar">
        <button type="button" className="new-chat-row" onClick={handleNewChat}>
          <svg className="new-chat-row-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M16.7929 2.79289C18.0118 1.57394 19.9882 1.57394 21.2071 2.79289C22.4261 4.01184 22.4261 5.98815 21.2071 7.20711L12.7071 15.7071C12.5196 15.8946 12.2652 16 12 16H9C8.44772 16 8 15.5523 8 15V12C8 11.7348 8.10536 11.4804 8.29289 11.2929L16.7929 2.79289ZM19.7929 4.20711C19.355 3.7692 18.645 3.7692 18.2071 4.20711L10 12.4142V14H11.5858L19.7929 5.79289C20.2308 5.35499 20.2308 4.64501 19.7929 4.20711ZM6 5C5.44772 5 5 5.44772 5 6V18C5 18.5523 5.44772 19 6 19H18C18.5523 19 19 18.5523 19 18V14C19 13.4477 19.4477 13 20 13C20.5523 13 21 13.4477 21 14V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V6C3 4.34315 4.34315 3 6 3H10C10.5523 3 11 3.44772 11 4C11 4.55228 10.5523 5 10 5H6Z" />
          </svg>
          <span className="new-chat-row-label">{t('newChat')}</span>
        </button>
        <div className="panel-tabs">
          <button
            className={`panel-tab${panelTab === 'history' ? ' active' : ''}`}
            onClick={() => setPanelTab('history')}
          >
            {t('historyTab') || 'História'}
          </button>
          <button
            className={`panel-tab${panelTab === 'questions' ? ' active' : ''}`}
            onClick={() => setPanelTab('questions')}
          >
            {t('questionsTab') || 'Otázky'}
          </button>
        </div>
      </div>

      {/* ══ TAB: Otázky (Moje otázky + Zdieľané otázky) ══════════════════ */}
      {panelTab === 'questions' && (
        <>
          {ownPrivate.length === 0 && sharedAll.length === 0 && (
            <div className="panel-empty">{t('noQuestionsYet') || 'Zatiaľ žiadne otázky.'}</div>
          )}

          {/* ── Moje otázky (súkromné) ── */}
          {ownPrivate.length > 0 && (
            <div id="group-2" className="panel-group-wrapper open">
              <div className="question-panel-title sub" style={{ display: 'flex', alignItems: 'center', height: '2.4em' }}>
                <h2 style={{ margin: 0 }}>{t('myQuestions') || 'Moje otázky'}</h2>
              </div>
              <ol className="question-list">
                {ownPrivate.map(q => (
                  <div
                    key={q.Id}
                    className="question-item table separator"
                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClick={() => handleQuestionClick(q)}
                  >
                    <TruncatedText
                      text={q.Question}
                      className="question-hover"
                      style={{ display: 'block', width: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    />
                    <div className="q-controls">
                      <span
                        className="material-symbols-outlined q-ctrl"
                        title={t('publish') || 'Zdieľať vo firme'}
                        onClick={(e) => handleShareCopy(e, q)}
                      >
                        share
                      </span>
                      <span
                        className="material-symbols-outlined q-ctrl delete"
                        title={t('delete') || 'Vymazať'}
                        onClick={(e) => handleDeleteQuestion(e, q)}
                      >
                        delete
                      </span>
                    </div>
                  </div>
                ))}
              </ol>
            </div>
          )}

          {/* ── Zdieľané otázky (zverejnené – moje aj kolegov; zbaliteľné) ── */}
          {sharedAll.length > 0 && (
            <div id="group-3" className={`panel-group-wrapper open${sharedOpen ? '' : ' collapsed'}`}>
              <div
                className={`question-panel-title sub collapsible-title${sharedOpen ? '' : ' collapsed'}`}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setSharedOpen(o => !o)}
              >
                <h2 style={{ margin: 0 }}>
                  {t('sharedQ')} <span className="shared-count">({sharedAll.length})</span>
                </h2>
                <span
                  className="material-symbols-outlined chevron"
                  style={{ transition: '0.2s transform', transform: sharedOpen ? 'rotate(180deg)' : 'none' }}
                >
                  keyboard_arrow_down
                </span>
              </div>
              {sharedOpen && (
                <ol className="question-list">
                  {sharedAll.map(q => (
                    <div
                      key={q.Id}
                      className="question-item table separator"
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => handleQuestionClick(q)}
                    >
                      <TruncatedText
                        text={q.Question}
                        className="question-hover"
                        style={{ display: 'block', width: '75%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      />
                      <div className="q-controls">
                        {!q.IsOwner && (
                          <span
                            className="material-symbols-outlined q-ctrl"
                            title={t('addToMy') || 'Pridať do mojich otázok'}
                            onClick={(e) => handleAddToMyList(e, q)}
                          >
                            bookmark_add
                          </span>
                        )}
                        <span
                          className="material-symbols-outlined q-ctrl delete"
                          title={t('delete') || 'Vymazať'}
                          onClick={(e) => handleDeleteQuestion(e, q)}
                        >
                          delete
                        </span>
                      </div>
                    </div>
                  ))}
                </ol>
              )}
            </div>
          )}
        </>
      )}

      {/* ══ TAB: História chatu (konverzácie) ═══════════════════════════ */}
      {panelTab === 'history' && (
      <div id="group-1" className={`panel-group-wrapper open`}>
        <ol className="question-list">
        {getVisibleSessions().map((session) => {
          const sId = session.id || session.Id;
          let sTitle;
          if (animatingTitle && sId === animatingTitle.sessionId) {
            sTitle = animatingTitle.displayedText;
          } else {
            sTitle = session.title || session.Title;
            // Normalizuj len PRESNÝ defaultný názov, nie používateľom premenované (napr. "Nový chat 2")
            if (!sTitle || sTitle === 'New Chat' || sTitle === 'Nový chat') {
              sTitle = t('newChat');
            }
          }
          const isPinned = session.pinOrder != null;
          return (
            <div
              key={`hist-${sId}`}
              className={`question-item table separator${activeSessionId === sId ? ' session-active' : ''}`}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onClick={() => handleSessionClick(sId)}
            >
              {renamingId === sId ? (
                <input
                  className="session-rename-input"
                  value={renameValue}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleRenameSubmit(sId); }
                    else if (e.key === 'Escape') { setRenamingId(null); }
                  }}
                  onBlur={() => handleRenameSubmit(sId)}
                />
              ) : (
                <TruncatedText
                  text={sTitle}
                  className="question-hover"
                  style={{ cursor: 'pointer', display: 'block', width: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                />
              )}
              {renamingId !== sId && (
                <>
                  <div
                    className={`pin-question${isPinned ? ' pinned' : ''}`}
                    title={isPinned ? t('unpin') || 'Unpin' : t('pin') || 'Pin'}
                    onClick={(e) => handlePinSession(e, sId)}
                  >
                    <PinIcon />
                  </div>
                  <div
                    className={`session-menu-btn material-symbols-outlined${menuState && menuState.id === sId ? ' open' : ''}`}
                    title={t('more') || 'More'}
                    onClick={(e) => handleMenuToggle(e, sId)}
                  >
                    more_horiz
                  </div>
                </>
              )}
            </div>
          );
        })}
        </ol>
      </div>
      )}

      {menuState && (
        <div
          className="session-menu-dropdown"
          style={{ top: menuState.top, left: menuState.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="session-menu-item"
            onClick={() => {
              const s = chatSessions.find(x => (x.id || x.Id) === menuState.id);
              setRenameValue((s && (s.title || s.Title)) || '');
              setRenamingId(menuState.id);
              setMenuState(null);
            }}
          >
            <span className="material-symbols-outlined">edit</span>
            {t('rename') || 'Rename'}
          </div>
          <div
            className="session-menu-item delete"
            onClick={() => {
              const s = chatSessions.find(x => (x.id || x.Id) === menuState.id);
              const title = s ? (s.title || s.Title) : '';
              setMenuState(null);
              handleDeleteSession(menuState.id, title);
            }}
          >
            <span className="material-symbols-outlined">delete</span>
            {t('delete') || 'Delete'}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={sessionToDelete !== null}
        onClose={handleCancelDelete}
        onNo={handleCancelDelete}
        onYes={handleConfirmDelete}
        item={sessionToDelete ? sessionToDelete.title : ''}
      />

      <ConfirmDeleteModal
        open={questionToDelete !== null}
        onClose={() => setQuestionToDelete(null)}
        onNo={() => setQuestionToDelete(null)}
        onYes={handleConfirmDeleteQuestion}
        item={questionToDelete ? questionToDelete.text : ''}
      />
    </div>
  );
}
                
export default ChatHistory;
